import React, {
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Table } from "../../../common";
import { Link } from "react-router-dom";
import {
  getInvoiceStatus,
  getInvoiceReoccur,
  formatMoney,
} from "../../../../utils";
import { IInvoice, SalesStatus } from "../../../../@types";
import dayjs from "dayjs";
import {
  AddSquareIcon,
  DownloadIcon,
  FilterIcon,
} from "../../../../assets/icons";
import { IPaginationMeta } from "../../../../@types";
import InvoiceFilter from "../InvoiceFilter";
import { DropDown } from "../../../common";
import HeadingIcon from "../../../../assets/icons/HeadingIcon";
import { BUSINESS_TYPE_RETAIL, Statuses } from "../../../../constants";
import { useNavigate } from "react-router-dom";
import { Filter, IRecurringInvoiceFilter } from "../../../../@types/invoice";
import {
  getRecurringStatusClass,
  getRecurringStatusColor,
  getStatusClass,
  getStatusColor,
} from "../Status/status";
import DropdownButton from "../../../common/Table/MobileTableComponent/DropdownOptions";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { MobileTable } from "../../../common/mobileTable";
import { getActiveSubscriptionWithHandling } from "../../../../backend-services/subscription";
import { SubscriptionModal } from "../../../common/Subscription/SubscriptionModal";
import { MobileSubscriptionModal } from "../../../common/Subscription/MobileSubscriptionModal";
import { useSubscriptionStore, useUserStore } from "../../../../state";
import { checkSubscription } from "../../../../utils/subscription";
import { servicesType } from "../../../../constants/Subscription";
import ActionModal from "../../../common/ActionModal";
import UploadIcon from "../../../../assets/icons/svgicons/UploadIcon.svg";
import { useCompanyStore } from "../../../../state/useCompanyStore";
import { columns } from "../../../../utils/recurring-invoice-list";

interface InvoicesTableProps {
  invoices: IInvoice[];
  isLoading: boolean;
  fetchMoreData: (reoccurringEntityId: string) => Promise<void>;
  meta: IPaginationMeta;
  onSearchData?: (text: string) => void;
  handleSort?: (text: string) => void;
  handleSearch?: (text: string) => void;
  showHead?: boolean;
  handleFilter?: (filter: Filter) => void;
  newLink?: string;
  title?: string;
  error?: boolean;
  isSearching?: boolean;
  headings?: {
    [key: string]: string;
  };
  data?: any[];
  searchResult: any[];
  selectedFilter?: Filter | IRecurringInvoiceFilter | undefined;
  searchedDone?: boolean;
  setSearchedDone?: (searchedDone: boolean) => void;
  reoccurringEntityId?: string | undefined;
}

const RecurringHistoryTable = ({
  selectedFilter,
  invoices,
  isLoading,
  fetchMoreData,
  meta,
  handleSort,
  handleSearch,
  searchResult,
  onSearchData,
  handleFilter,
  isSearching,
  searchedDone,
  setSearchedDone,
  reoccurringEntityId,
}: InvoicesTableProps) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [showFilter, setShowFilter] = React.useState(false);
  const [isReoccurring, setIsReoccurring] = React.useState<boolean | null>(
    null,
  );
  const [showSubscriptionModal, setShowSubscriptionModal] =
    React.useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    React.useState<string>("");
  const [status, setStatus] = React.useState<SalesStatus | "">("");
  const [dropdown, setDropdown] = React.useState(false);
  const { subscriptionData } = useSubscriptionStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { company } = useCompanyStore();
  const businessType = company?.businessType || "";
  const isRetailBusiness = businessType === BUSINESS_TYPE_RETAIL;
  const handleFilterIconClick = () => {
    setShowFilter(true);
  };

  const handleCloseModal = () => {
    setShowFilter(false);
  };

  const getDaySuffix = (day: number) => {
    if (day >= 11 && day <= 13) {
      return day + "th";
    }

    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  };
  const navigate = useNavigate();
  const handleSearchSelect = (item: any) => {
    item.status === Statuses.DRAFT.toLowerCase()
      ? navigate(`/invoices/${item.id}`)
      : navigate(`/invoices/${item.id}/recurringView`);
  };
  const observer = useRef<IntersectionObserver | null>(null);
  const lastInvoiceRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && meta.hasMoreData) {
          fetchMoreData(reoccurringEntityId || "");
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, meta],
  );

  const fetchData = () => {
    const subscriptionCheck = checkSubscription(servicesType.Invoices);
    if (subscriptionCheck.status) {
      setIsModalOpen(true);
      return;
    }
    setShowSubscriptionModal(true);
    setSubscriptionErrorMessage(subscriptionCheck.message || "");
  };

  const memoizedSelectedFilter = useMemo(
    () => selectedFilter,
    [
      selectedFilter && "status" in selectedFilter
        ? selectedFilter.status
        : null,
      selectedFilter && "isReoccurring" in selectedFilter
        ? selectedFilter.isReoccurring
        : null,
    ],
  );

  const renderInvoicesData = (
    invoices: IInvoice[],
    reoccurringEntityId: string,
  ) => {
    const filteredInvoices = invoices.filter(
      (invoice: IInvoice) =>
        invoice.reoccurringEntityId === reoccurringEntityId,
    );

    return filteredInvoices.map((invoice: IInvoice, index) => {
      const status = invoice.reoccurringStatus || "Unknown";
      const statusCapitalized =
        status.charAt(0).toUpperCase() + status.slice(1);

      const linkText = invoice.invoiceNumber;
      const isDraft = statusCapitalized === Statuses.DRAFT;
      const linkTo = isDraft
        ? `/invoices/${invoice.id}`
        : `/invoices/${invoice.id}/view`;
      const invoiceId = invoice.id;

      return {
        invoiceNumbers: (
          <div ref={index === invoices.length - 1 ? lastInvoiceRef : null}>
            <Link className="text-violet-800" to={linkTo}>
              {linkText}
            </Link>
          </div>
        ),
        customer: (
          <span className="">
            {`${invoice?.customer?.firstName || ""} ${invoice?.customer?.lastName || ""}`}
          </span>
        ),
        issueDate: `${dayjs(invoice.issueDate).format("MMM")} ${getDaySuffix(dayjs(invoice.issueDate).date())} ${dayjs(
          invoice.issueDate,
        ).format("YYYY")}`,
        invoiceNumber: invoice.invoiceNumber,
        invoiceId: invoiceId,
        amount: formatMoney(invoice.balanceDue, invoice.currency),
      };
    });
  };

  const renderInvoicesTableHead = useMemo(
    () => ({
      invoiceNumbers: { title: "Invoice ID" },
      issueDate: { title: "Invoice Date" },
      amount: { title: "Amount" },
    }),
    [],
  );
  const handleStatusChange = (newStatus: SalesStatus) => {
    setStatus(newStatus.toLowerCase() as SalesStatus);
  };

  const handleIsReoccurringChange = (newIsReoccurring: boolean) => {
    setIsReoccurring(newIsReoccurring);
  };

  const onInvoiceFilterSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (handleFilter) {
      handleFilter({ status, isReoccurring });
    }
    setShowFilter(false);
  };

  const fetchPageData = (direction: "up" | "down") => {
    if (direction === "down" && meta.hasMoreData) {
      fetchMoreData(reoccurringEntityId || "");
    }
  };

  const onSearch = (text: string) => {
    if (onSearchData && handleSearch) {
      handleSearch(text);
    }
  };

  const handleNewInvoiceClick = () => {
    const subscriptionCheck = checkSubscription(servicesType.Invoices);
    if (subscriptionCheck.status) {
      navigate("/invoices/create");
      return;
    }
    setShowSubscriptionModal(true);
    setSubscriptionErrorMessage(subscriptionCheck.message || "");
  };

  const handleInventoryInvoiceClick = async () => {
    const subscriptionCheck = checkSubscription(servicesType.Invoices);
    if (subscriptionCheck.status) {
      navigate("/invoices/create");
      return;
    } else {
      setShowSubscriptionModal(true);
    }
  };

  const handleFreeformInvoiceClick = () => {
    const subscriptionCheck = checkSubscription(servicesType.Invoices);
    if (subscriptionCheck.status) {
      navigate("/invoices/createFreeform");
    } else {
      setShowSubscriptionModal(true);
      setSubscriptionErrorMessage(subscriptionCheck.message || "");
    }
  };

  const columns = [
    {
      key: "invoiceNumbers",
      label: "Invoice",
      className: "",
      width: 8,
    },
    {
      key: "customer",
      label: "Customer Name",
      className: "",
      width: 26,
    },
    {
      key: "issueDate",
      label: "Issue Date",
      className: "",
      width: 15,
    },
    {
      key: "amount",
      label: "Balance Due",
      className: "",
      width: 20,
    },
  ];

  const isMobile = window.innerWidth < 768;
  return (
    <>
      {isDesktop ? (
        <div className="hidden md:block whitespace-nowrap w-full">
          <Table
            searchPlaceHolder="Search by customer name"
            selectedFilter={selectedFilter}
            handleFilter={handleFilter}
            searchDisplayKey="customer.firstName"
            searchResult={searchResult}
            head={renderInvoicesTableHead}
            data={renderInvoicesData(invoices, reoccurringEntityId || "")}
            isLoading={isLoading}
            isSearching={isSearching}
            handleSearchSelect={handleSearchSelect}
            searchedDone={searchedDone}
            setSearchedDone={setSearchedDone}
            buttons={[
              {
                text: "",
                icon: (
                  <DropDown
                    onChange={handleFilterIconClick}
                    show={showFilter}
                    component={
                      <div className="flex ">
                        {" "}
                        <span>Filter</span> <FilterIcon color="#044E97" />{" "}
                      </div>
                    }
                  >
                    <InvoiceFilter
                      isReoccurring={
                        memoizedSelectedFilter &&
                        "isReoccurring" in memoizedSelectedFilter
                          ? memoizedSelectedFilter.isReoccurring
                          : undefined
                      }
                      setStatus={handleStatusChange}
                      setIsReoccurring={handleIsReoccurringChange}
                      status={
                        memoizedSelectedFilter &&
                        "status" in memoizedSelectedFilter
                          ? memoizedSelectedFilter.status
                          : undefined
                      }
                      onSubmit={onInvoiceFilterSubmit}
                      onClose={handleCloseModal}
                      showFilter
                      //@ts-ignore
                      handleFilter={handleFilter}
                    />
                  </DropDown>
                ),
              },
            ]}
            showPagination={true}
            handleSearch={onSearch}
            handleSort={handleSort}
            fetchMoreData={fetchPageData}
            meta={meta}
            columns={columns}
          />
        </div>
      ) : (
        <div className="md:hidden mt-[12px] w-full">
          <div className="md:hidden w-full">
            <MobileTable
              searchPlaceHolder="Search by customer"
              selectedFilter={selectedFilter}
              handleFilter={handleFilter}
              searchDisplayKey="customer.firstName"
              searchResult={searchResult}
              head={renderInvoicesTableHead}
              data={renderInvoicesData(invoices, reoccurringEntityId || "")}
              isLoading={isLoading}
              isSearching={isSearching}
              handleSearchSelect={handleSearchSelect}
              hideFloatingScrollButton={true}
              buttons={[
                {
                  text: "",
                  icon: "",
                },
              ]}
              showPagination={true}
              handleSearch={onSearch}
              handleSort={handleSort}
              fetchMoreData={fetchPageData}
              meta={meta}
              invoiceCard={true}
            />
          </div>
        </div>
      )}

      <div className="mt-[]">
        {isMobile && showSubscriptionModal && (
          <SubscriptionModal>
            <MobileSubscriptionModal
              onClose={() => setShowSubscriptionModal(false)}
              titleText={`Need to access more features?`}
              subtitleText={subscriptionErrorMessage}
            />
          </SubscriptionModal>
        )}
      </div>
    </>
  );
};

export default RecurringHistoryTable;
